import { CloudUploadOutlined } from "@ant-design/icons";
import { App, Button, Empty, Flex, Image, List, Typography } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useAppDispatch, useAppSelector, useInfiniteScroll } from "@/hooks";
import {
    photoStorage,
    StoredPhoto,
    StoredPhotoMeta,
} from "@/Services/PhotoStorage";
import {
    setNextPhotoUploadRetryAt,
    setPhotoUploadRetryCandidatesCount,
} from "@/store/slices/offline";
import { handleDownloadFile } from "@/utils/file/handleDownloadFile";
import { isOnline } from "@/utils/network/isOnline";
import { isNeedRetry } from "@/utils/photo/photoUploadRetry";

import { PhotoListItem } from "./PhotoListItem";

const MAX_ALLOWED_LATENCY_MS = 2000;

export const PhotoList = () => {
    const { notification } = App.useApp();

    const { offlineMode, requestQueueStatus } = useAppSelector(
        (state) => state.offline,
    );
    const dispatch = useAppDispatch();

    const [dbPhotos, setDbPhotos] = useState<StoredPhotoMeta[]>([]);
    const [activePhoto, setActivePhoto] = useState<StoredPhoto | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const candidates = useMemo(() => dbPhotos.filter(isNeedRetry), [dbPhotos]);

    const { visibleItems, loadMore } = useInfiniteScroll(candidates, 5);

    useEffect(() => {
        let cancelled = false;

        void (async () => {
            try {
                setLoading(true);
                setError("");

                const photos = await photoStorage.getAllPhotosMeta();
                if (cancelled) return;

                setDbPhotos(photos);
                dispatch(
                    setPhotoUploadRetryCandidatesCount(
                        photos.filter(isNeedRetry).length,
                    ),
                );
            } catch (e) {
                console.error(e);
                if (!cancelled) {
                    setError("Не вдалося отримати фото із бази даних");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [dispatch]);

    const handleUploadAllStub = useCallback(async () => {
        const isGoodLatency = await isOnline(MAX_ALLOWED_LATENCY_MS);
        if (!isGoodLatency) {
            notification.warning({
                title: "Завантаження фото тимчасово зупинено",
                description:
                    "Мережа відповідає занадто повільно, тому ми зупинили відправку, щоб уникнути помилок. Відновіть інтернет і повторіть спробу.",
            });

            return;
        }
        dispatch(
            setNextPhotoUploadRetryAt({
                time: dayjs().toISOString(),
                ignoreBackoff: true,
            }),
        );
    }, [notification, dispatch]);

    return (
        <Flex vertical gap={12}>
            {candidates.length > 0 && (
                <Flex align="center" justify="space-between">
                    <Typography.Title level={5} style={{ margin: 0 }}>
                        Фото в черзі: {candidates.length}
                    </Typography.Title>

                    <Button
                        type="primary"
                        icon={<CloudUploadOutlined />}
                        disabled={offlineMode || requestQueueStatus.isRunning}
                        onClick={handleUploadAllStub}
                    >
                        Завантажити всі
                    </Button>
                </Flex>
            )}

            {error && <Typography.Text type="danger">{error}</Typography.Text>}

            {candidates.length === 0 ? (
                <Empty description="Черга порожня — немає фото для завантаження" />
            ) : (
                <Image.PreviewGroup
                    preview={{
                        toolbarRender: () => (
                            <Button
                                type="primary"
                                disabled={!activePhoto}
                                onClick={() => {
                                    if (!activePhoto) return;
                                    handleDownloadFile(
                                        activePhoto.base64,
                                        activePhoto.name,
                                    );
                                }}
                            >
                                Скачати
                            </Button>
                        ),
                    }}
                >
                    <List
                        loading={loading}
                        itemLayout="horizontal"
                        dataSource={visibleItems}
                        renderItem={(p, index) => (
                            <PhotoListItem
                                photo={p}
                                setActivePhoto={setActivePhoto}
                                isLast={index === visibleItems.length - 1}
                                onInView={loadMore}
                            />
                        )}
                    />
                </Image.PreviewGroup>
            )}
        </Flex>
    );
};
